import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Dialog from '../ui/Dialog';
import Field, { FormErrorSummary, FormServerError, rules, useFormValidation, type FormSchema } from '../ui/Field';
import type { FieldRule } from '../ui/fieldModel';
import { FormFooter } from './formBits';
import { api } from '../../services/api';
import { paymentTermOptions } from '../../utils/paymentTerms';
import { scheduleKey } from './projectModel';
import type { PlannedHoursMode } from '../../types';
import HideFromCustomerToggle from '../HideFromCustomerToggle';
import PendingDecision from '../ui/PendingDecision';
import ServerTermsWarning from '../engagement/ServerTermsWarning';
import { withoutInternalOnlyOnCustomerWork } from '../../utils/customerWorkPeople';
import EntityPicker from '../ui/EntityPicker';
import { Building2 } from 'lucide-react';
import DateInput from '../ui/DateInput';
import { usePermissions } from '../../hooks/usePermissions';
import { useInternalOrgId } from '../../hooks/useInternalOrg';
import { useAuthStore } from '../../store/authStore';
import WarrantyField from './WarrantyField';
import { warrantyOf, warrantyPatch, type WarrantyValue } from './projectModel';
import { CUSTOMERS_GROUP_KEY, INTERNAL_WORK_LABEL_KEY, internalFirstPickerOptions, isCompanyOrg } from '../../utils/internalWorkOption';

export type ProjectKind = 'project' | 'development';

const MODES: PlannedHoursMode[] = ['undefined', 'automatic', 'manual'];

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const dateStr = (v: unknown): string => str(v).slice(0, 10);
const num = (s: string): number | null => (s.trim() === '' ? null : Number(s.replace(',', '.')));
/** A number ≥ 0 (money, hours). */
const nonNegative: FieldRule = (v) => {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? null : { key: 'engagementForm.nonNegative' };
};

/** The due date is not before the start date (the same day is fine). */
const notBefore = (otherId: string): FieldRule => (v, all) => {
  const a = String(v ?? '');
  const b = String(all[otherId] ?? '');
  return a && b && a < b ? { key: 'projectForm.endBeforeStart' } : null;
};

interface Values {
  organization_id: string;
  name: string;
  project_manager_id: string;
  team_lead_id: string;
  description: string;
  start_date: string;
  end_date: string;
  planned_hours_mode: PlannedHoursMode;
  planned_hours_manual: string;
  fixed_price: string;
  server_monthly_fee: string;
  /** P-31b (D-180): what the server costs OTE per month (an expense). */
  server_monthly_cost: string;
  payment_terms: string;
}

/**
 * P-04/B (D-31) — the form of a new project / development, and its edit.
 *
 * Opened from: the customer page ("+ הוספת התקשרות ▾ ← פרויקט / פיתוח", the
 * "+ פרויקט חדש" / "+ פיתוח חדש" of its tabs — the customer is locked) and
 * "פרויקט חדש" on /projects (an active customer is picked — D-30). Edit: the
 * project page's [✎ עריכה], every field; the status is not part of the form.
 *
 * Price and payment terms are required except for the internal customer
 * (D-42). The payment schedule is fixed by the type (D-06) and read-only.
 * Creating writes the engagement + its row (+ the 4 core milestones of a
 * project) in one request; the caller then opens the project page.
 */
export default function ProjectFormModal({
  mode, kind, organizationId, organizationName, project, initialName, characterizationId, canEditCommercial = true, showFinancials = true, onClose, onSaved,
}: {
  mode: 'create' | 'edit';
  kind: ProjectKind;
  /** create: the customer, locked (from the customer page); absent = pick one. */
  organizationId?: string;
  organizationName?: string;
  /** edit: the project (GET /projects/:id). */
  project?: Record<string, any> | null;
  /** create: a name already typed elsewhere (the general engagement form). */
  initialName?: string;
  /** P-07 (D-74 §8) — create: opened from an "אפיון" card; the server links it in the same transaction. */
  characterizationId?: string;
  /** edit: the project manager and the money need a direct customer link (GM / Account Manager). */
  canEditCommercial?: boolean;
  /** edit: who may not see the financials does not get the finance section. */
  showFinancials?: boolean;
  onClose: () => void;
  onSaved: (projectId: string) => void;
}) {
  const { t } = useTranslation();
  // D-274 §2 — an internal-only employee opens a project of "עבודה פנימית OTE" only: the customer is fixed.
  const { internalOnly, internalWorkOrganizationId } = usePermissions();
  const internalOrgId = useInternalOrgId();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const fixedInternal = mode === 'create' && !organizationId && internalOnly && !!internalWorkOrganizationId;
  const lockedCustomer = mode === 'edit' || !!organizationId || fixedInternal;

  const { data: orgsRes } = useQuery({
    queryKey: ['organizations-for-project', 'active', 'with-internal'],
    // D-274 §2 — "עבודה פנימית OTE" is a choice for every OTE employee.
    queryFn: () => api.getOrganizations({ limit: 200, status: 'active', include_internal: 1 }),
    enabled: !lockedCustomer,
    staleTime: 60_000,
  });
  const customers: Array<{ id: string; name: string; is_internal?: boolean }> = orgsRes?.data ?? [];

  const lockedOrgId = mode === 'edit' ? str(project?.organization_id) : organizationId ?? (fixedInternal ? String(internalWorkOrganizationId) : '');
  const { data: lockedOrgRes } = useQuery({
    queryKey: ['organization', lockedOrgId],
    queryFn: () => api.getOrganization(lockedOrgId),
    enabled: lockedCustomer && !!lockedOrgId && !fixedInternal,
  });

  const { data: employeesRes } = useQuery({
    queryKey: ['users', 'ote_staff', 'all-active'],
    queryFn: () => api.getUsers({ ote_staff: true, limit: 200 }),
    staleTime: 60_000,
  });

  const [values, setValues] = useState<Values>(() => ({
    organization_id: lockedOrgId,
    name: str(project?.name ?? initialName),
    project_manager_id: str(project?.project_manager_id),
    team_lead_id: str(project?.team_lead_id),
    description: str(project?.description),
    start_date: dateStr(project?.start_date),
    end_date: dateStr(project?.end_date),
    planned_hours_mode: (project?.planned_hours_mode as PlannedHoursMode) || 'undefined',
    planned_hours_manual: str(project?.planned_hours_manual),
    fixed_price: str(project?.fixed_price),
    server_monthly_fee: str(project?.server_monthly_fee),
    server_monthly_cost: str(project?.server_monthly_cost),
    payment_terms: str(project?.payment_terms),
  }));
  const set = <K extends keyof Values>(key: K, value: Values[K]) => setValues((prev) => ({ ...prev, [key]: value }));

  const customer = lockedCustomer
    ? { id: lockedOrgId, name: organizationName ?? lockedOrgRes?.data?.name ?? str(project?.organization_name), is_internal: lockedOrgRes?.data?.is_internal ?? project?.organization_is_internal }
    : customers.find((c) => c.id === values.organization_id);
  // D-274 §2 — the company: by its flag, or by its id (the internal-only employee's fixed customer).
  const internal = customer?.is_internal === true || fixedInternal
    || (!!values.organization_id && isCompanyOrg({ id: values.organization_id }, internalOrgId));
  // D-274 §2 — the creator is the project manager of an internal project (changeable).
  useEffect(() => {
    if (mode === 'create' && internal && !values.project_manager_id && currentUserId) set('project_manager_id', currentUserId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, internal, currentUserId]);

  // Active OTE employees; a current manager / team lead who is no longer one stays visible.
  const employees = useMemo(() => {
    // FIX-B17 (D-198 QX-152) — a customer's project: no "פנימי בלבד" manager / team lead.
    const list = withoutInternalOnlyOnCustomerWork((employeesRes?.data ?? []) as any[], !internal).filter((u: any) => u.active !== false).map((u: any) => ({ id: u.id as string, name: (u.name || u.email) as string }));
    for (const [id, name] of [[project?.project_manager_id, project?.project_manager_name], [project?.team_lead_id, project?.team_lead_name]]) {
      if (id && !list.some((u) => u.id === id)) list.push({ id: String(id), name: str(name) || String(id) });
    }
    return list.sort((a, b) => a.name.localeCompare(b.name, 'he'));
  }, [employeesRes, project, internal]);

  // D-274 §2 (D-42) — a project of "עבודה פנימית OTE" has no price, terms, schedule or server charge.
  const finance = (mode === 'create' || showFinancials) && !internal;
  const commercialLocked = mode === 'edit' && !canEditCommercial;

  const schema: FormSchema = {
    ...(lockedCustomer ? {} : { 'pf-customer': [rules.required()] }),
    'pf-name': [rules.required(), rules.minLength(2)],
    'pf-pm': [rules.required()],
    'pf-start': [rules.required()],
    'pf-end': [rules.required(), notBefore('pf-start')],
    ...(values.planned_hours_mode === 'manual' ? { 'pf-planned': [rules.required(), nonNegative] } : {}),
    ...(finance ? {
      'pf-price': internal ? [nonNegative] : [rules.required(), nonNegative],
      'pf-server': [nonNegative],
      'pf-server-cost': [nonNegative],
      ...(internal ? {} : { 'pf-terms': [rules.required()] }),
    } : {}),
  };
  const formValues = {
    'pf-customer': values.organization_id,
    'pf-name': values.name,
    'pf-pm': values.project_manager_id,
    'pf-start': values.start_date,
    'pf-end': values.end_date,
    'pf-planned': values.planned_hours_manual,
    'pf-price': values.fixed_price,
    'pf-server': values.server_monthly_fee,
    'pf-server-cost': values.server_monthly_cost,
    'pf-terms': values.payment_terms,
  };
  const v = useFormValidation(schema, formValues);
  const labels: Record<string, string> = {
    'pf-customer': t('projectForm.customer'),
    'pf-name': t('projectForm.name'),
    'pf-pm': t('projectForm.projectManager'),
    'pf-start': t('projectForm.startDate'),
    'pf-end': t('projectForm.endDate'),
    'pf-planned': t('projectForm.plannedManual'),
    'pf-price': t('projectForm.price'),
    'pf-server': t('projectForm.serverFee'),
    'pf-server-cost': t('projectForm.serverCost'),
    'pf-terms': t('projectForm.paymentTerms'),
  };

  const body = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {
      name: values.name.trim(),
      description: values.description.trim() || null,
      start_date: values.start_date,
      end_date: values.end_date,
      team_lead_id: values.team_lead_id || null,
      planned_hours_mode: values.planned_hours_mode,
      planned_hours_manual: values.planned_hours_mode === 'manual' ? num(values.planned_hours_manual) : null,
    };
    if (!commercialLocked) out.project_manager_id = values.project_manager_id;
    if (finance && !commercialLocked) {
      out.fixed_price = num(values.fixed_price);
      out.server_monthly_fee = num(values.server_monthly_fee);
      out.server_monthly_cost = num(values.server_monthly_cost);
      out.payment_terms = values.payment_terms || null;
    }
    return out;
  };

  // D-303 / D-304 — "אחריות ללקוח" (edit only; empty by default). Ending the project asks for it (the status window).
  const [warranty, setWarranty] = useState<WarrantyValue>(() => warrantyOf(project));
  const warrantyChanged = mode === 'edit' && (() => {
    const before = warrantyOf(project);
    return before.until !== warranty.until || before.none !== warranty.none;
  })();

  const mutation = useMutation({
    mutationFn: async () => {
      if (mode === 'create') {
        const r = await api.createProject({
          organization_id: values.organization_id, type: kind, ...(body() as any),
          ...(characterizationId ? { characterization_id: characterizationId } : {}),
        });
        return String(r.data?.id ?? '');
      }
      // Only what changed is sent (the server applies only changes anyway).
      const b = body();
      const changed: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(b)) {
        const before = project?.[k];
        const same = k.endsWith('_date') ? dateStr(before) === str(val)
          : typeof val === 'number' ? before !== null && before !== undefined && before !== '' && Number(before) === val
          : (before ?? null) === (val ?? null) || (before ?? '') === (val ?? '');
        if (!same) changed[k] = val;
      }
      if (warrantyChanged) Object.assign(changed, warrantyPatch(warranty));
      if (Object.keys(changed).length > 0) await api.updateProject(String(project!.id), changed as any);
      return String(project!.id);
    },
    onSuccess: (id) => onSaved(id),
  });

  const submit = v.submit(() => mutation.mutate());
  const customerName = internal ? t(INTERNAL_WORK_LABEL_KEY) : (customer?.name ?? '');
  const title = mode === 'create'
    ? (customerName
      ? t(kind === 'development' ? 'projectForm.titleNewDevelopment' : 'projectForm.titleNewProject', { customer: customerName })
      : t(kind === 'development' ? 'projectForm.titleNewDevelopmentBare' : 'projectForm.titleNewProjectBare')) // DQ-181: no dangling dash before a customer is chosen
    : t('projectForm.titleEdit', { name: str(project?.name) });
  const scheduleLabel = t(scheduleKey(kind));

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      size="lg"
      dismissible={!mutation.isPending}
      footer={<FormFooter formId="project-form" busy={mutation.isPending} submitLabel={mode === 'create' ? t('projectForm.create') : t('common.save')} onCancel={onClose} />}
    >
      <form id="project-form" onSubmit={submit} noValidate className="space-y-5">
        {v.showSummary && <FormErrorSummary errors={v.errors} labels={labels} order={Object.keys(schema)} />}
        <FormServerError error={mutation.error} fallback={t(mode === 'create' ? 'projectForm.createError' : 'projectForm.saveError')} />

        {fixedInternal && (
          <Field id="pf-customer" label={labels['pf-customer']}>
            <p className="flex min-h-touch items-center gap-1.5 text-sm text-text" aria-readonly="true"><bdi>{t(INTERNAL_WORK_LABEL_KEY)}</bdi></p>
          </Field>
        )}
        {!lockedCustomer && (
          <Field id="pf-customer" label={labels['pf-customer']} required error={v.errors['pf-customer']}>
            <EntityPicker
              icon={Building2}
              placeholder={t('projectForm.pickCustomer')}
              options={internalFirstPickerOptions(customers, internalOrgId, t(INTERNAL_WORK_LABEL_KEY), t(CUSTOMERS_GROUP_KEY))}
              value={values.organization_id}
              onChange={(id) => set('organization_id', id)}
              onBlur={() => v.blur('pf-customer')}
            />
          </Field>
        )}

        <div className="grid gap-x-4 gap-y-4 sm:grid-cols-3">
          <Field id="pf-name" label={labels['pf-name']} required error={v.errors['pf-name']}>
            <input className="input" value={values.name} onChange={(e) => set('name', e.target.value)} onBlur={() => v.blur('pf-name')} autoComplete="off" />
          </Field>
          <Field id="pf-pm" label={labels['pf-pm']} required error={v.errors['pf-pm']}
            hint={commercialLocked ? t('projectForm.commercialLocked') : undefined}>
            <EntityPicker
              placeholder={t('projectForm.pickEmployee')}
              options={employees.map((u) => ({ value: u.id, label: u.name }))}
              value={values.project_manager_id}
              disabled={commercialLocked}
              onChange={(id) => set('project_manager_id', id)}
              onBlur={() => v.blur('pf-pm')}
            />
          </Field>
          <Field id="pf-tl" label={t('projectForm.teamLead')} optional>
            <EntityPicker
              emptyLabel={t('projectForm.noTeamLead')}
              options={employees.map((u) => ({ value: u.id, label: u.name }))}
              value={values.team_lead_id}
              onChange={(id) => set('team_lead_id', id)}
            />
          </Field>
        </div>

        <Field id="pf-desc" label={t('projectForm.description')}>
          <textarea className="input" rows={2} value={values.description} onChange={(e) => set('description', e.target.value)} />
        </Field>

        <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2">
          <Field id="pf-start" label={labels['pf-start']} required error={v.errors['pf-start']}>
            <DateInput dir="ltr" className="input text-start" value={values.start_date} onChange={(e) => set('start_date', e.target.value)} onBlur={() => v.blur('pf-start')} />
          </Field>
          <Field id="pf-end" label={labels['pf-end']} required error={v.errors['pf-end']}>
            <DateInput dir="ltr" className="input text-start" value={values.end_date} onChange={(e) => set('end_date', e.target.value)} onBlur={() => v.blur('pf-end')} />
          </Field>
        </div>

        {mode === 'edit' && !internal && (
          <WarrantyField id="pf-warranty" value={warranty} onChange={setWarranty} />
        )}

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-surface-700">{t('projectForm.plannedHours')}</legend>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {MODES.map((m) => (
              <label key={m} className="inline-flex items-center gap-2 text-sm text-surface-800">
                <input type="radio" name="pf-planned-mode" value={m} checked={values.planned_hours_mode === m} onChange={() => set('planned_hours_mode', m)} />
                {t(`projectForm.plannedModes.${m}`)}
              </label>
            ))}
            {values.planned_hours_mode === 'manual' && (
              <Field id="pf-planned" label={labels['pf-planned']} hideLabel required error={v.errors['pf-planned']} className="w-32">
                <input type="number" inputMode="decimal" min={0} step="0.5" className="input" value={values.planned_hours_manual}
                  onChange={(e) => set('planned_hours_manual', e.target.value)} onBlur={() => v.blur('pf-planned')} />
              </Field>
            )}
          </div>
        </fieldset>

        {finance && (
          <fieldset className="space-y-3 border-t border-surface-200 pt-4">
            <legend className="px-1 text-sm font-semibold text-surface-800">{t('projectForm.financeSection')}</legend>
            {internal && <p className="text-xs text-surface-600">{t('projectForm.internalHint')}</p>}
            {commercialLocked && <p className="text-xs text-surface-600">{t('projectForm.commercialLocked')}</p>}
            <div className="grid gap-x-4 gap-y-4 sm:grid-cols-3">
              <Field id="pf-price" label={labels['pf-price']} required={!internal} error={v.errors['pf-price']}>
                <input type="number" inputMode="decimal" min={0} step="0.01" className="input" value={values.fixed_price} disabled={commercialLocked}
                  onChange={(e) => set('fixed_price', e.target.value)} onBlur={() => v.blur('pf-price')} />
              </Field>
              <div className="relative">
                {/* D-125 PM-11 / PM-12: the server charge (partial month; in arrears or in advance) */}
                <span className="absolute end-0 top-0 inline-flex gap-1">
                  <PendingDecision id="PM-11" />
                  <PendingDecision id="PM-12" />
                </span>
                <Field id="pf-server" label={labels['pf-server']} error={v.errors['pf-server']}>
                  <input type="number" inputMode="decimal" min={0} step="0.01" className="input" value={values.server_monthly_fee} disabled={commercialLocked}
                    onChange={(e) => set('server_monthly_fee', e.target.value)} onBlur={() => v.blur('pf-server')} />
                </Field>
              </div>
              <Field id="pf-server-cost" label={labels['pf-server-cost']} error={v.errors['pf-server-cost']}>
                <input type="number" inputMode="decimal" min={0} step="0.01" className="input" value={values.server_monthly_cost} disabled={commercialLocked}
                  onChange={(e) => set('server_monthly_cost', e.target.value)} onBlur={() => v.blur('pf-server-cost')} />
              </Field>
              {/* P-34 (D-188): a cost with no price, or a price below the cost - a warning, never a block. */}
              <ServerTermsWarning fee={values.server_monthly_fee} cost={values.server_monthly_cost} />
              <Field id="pf-terms" label={labels['pf-terms']} required={!internal} error={v.errors['pf-terms']}>
                <select className="input" value={values.payment_terms} disabled={commercialLocked}
                  onChange={(e) => set('payment_terms', e.target.value)} onBlur={() => v.blur('pf-terms')}>
                  <option value="">{t('engagementForm.pickTerms')}</option>
                  {paymentTermOptions(values.payment_terms || null).map((p) => <option key={p} value={p}>{t(`paymentTerms.${p}`)}</option>)}
                </select>
              </Field>
            </div>
            <p className="text-sm text-surface-700">
              <span className="font-medium">{t('projectForm.schedule')}:</span> {scheduleLabel}{' '}
              <span className="text-surface-500">{t('projectForm.scheduleFixed')}</span>
            </p>
          </fieldset>
        )}
        {mode === 'edit' && <HideFromCustomerToggle entity="project" id={project?.id} />}
      </form>
    </Dialog>
  );
}
